/*
  The pinned product form reads server-rendered option ids, prices and shipping facts.
  Selection updates commit together; failed reads link to native option navigation.
  Adds share the cart queue and never retry an uncertain write. Custom sizes use
  Shopify's published price ladder; debossing uses the shop's stamp constraints.
  Without JavaScript, native choices reload the page and the form posts normally.
*/
import { areaSqIn, bandFor, sizeProperty, stitched } from './capra-deskpad-math.js';
import { Ladder } from './capra-deskpad-ladder.js';
import { ROOT, request, mutate } from './capra-cart.js';
import { validateDebossing } from './capra-debossing.js';

// Phones: the panel spans the bar. Wider: it hangs from the Customization button.
const PHONE = matchMedia('(max-width: 799px)');
const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const SWAPS = ['price', 'status', 'picker', 'ship', 'add', 'summary'];

class CapraProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.form.noValidate = true; // JS opens a closed option panel before reporting its required field.
    this.section = this.parentElement.closest('[data-section-id]') || document; // the page, not this element (it carries the id too)
    this.picker = this.querySelector('[data-picker]');
    this.input = this.form.querySelector('[data-variant-input]');
    if (this.input) this.input.disabled = false;
    this.which = null;
    this.customOn = false;

    this.form.addEventListener('change', (event) => this.onChange(event));
    this.form.addEventListener('submit', (event) => this.onSubmit(event));
    this.form.addEventListener('input', (event) => this.onType(event));
    this.addEventListener('click', (event) => this.onPick(event));
    this.form.addEventListener('invalid', () => { this.open('all'); this.focusDeboss(); }, true);
    // The unit switch in a custom size is the site's (capra-units): it has set the unit by the time this runs.
    this.addEventListener('click', (event) => {
      if (event.target.closest('[data-custom-panel] [data-unit]')) queueMicrotask(() => this.customUpdate());
    });
    // Light dismiss closes the panel before the click lands: remember what was open.
    this.addEventListener('pointerdown', (event) => {
      const button = event.target.closest('[data-pick]');
      this.pressedOpen = Boolean(button && this.isOpen() && this.which === button.dataset.pick);
    });
    this.picker?.addEventListener('toggle', (event) => this.onToggle(event));
    addEventListener('resize', () => this.isOpen() && this.place());
    // The keyboard rising or falling, or iOS scrolling the page to the field, moves the visible bottom.
    for (const type of ['resize', 'scroll']) {
      window.visualViewport?.addEventListener(type, () => {
        if (!this.isOpen()) return;
        this.place();
        if (this.picker.contains(document.activeElement)) this.revealDeboss('auto');
      });
    }
    // Arrows walk a radiogroup and choose as they go: landing on "Yes Please" there must not pull focus out.
    this.addEventListener('keydown', (event) => { this.arrowed = event.key.startsWith('Arrow'); });
    this.addEventListener('pointerdown', () => { this.arrowed = false; });
    this.syncDebossing();
  }

  isOpen() {
    return Boolean(this.picker?.matches(':popover-open'));
  }

  /* --- The option panel --- */

  onPick(event) {
    const button = event.target.closest('[data-pick]');
    if (!button || !this.picker) return;
    event.preventDefault();
    if (this.submitting || this.uncertain) return;
    const which = 'all';
    if (this.pressedOpen || (this.isOpen() && this.which === which)) {
      this.pressedOpen = false;
      if (this.isOpen()) this.picker.hidePopover();
      return;
    }
    this.open(which, button);
  }

  open(which, button) {
    this.which = which;
    this.opener = button || this.querySelector(`[data-pick="${which}"]`);
    this.only();
    this.place();
    if (!this.isOpen()) this.picker.showPopover();
    (this.picker.querySelector('.picker__group:not([hidden]) .picker__input:checked') ||
      this.picker.querySelector('.picker__group:not([hidden]) .picker__input:not(:disabled)'))?.focus({ preventScroll: true });
  }

  // A custom size keeps only its size and debossing groups visible.
  only() {
    if (!this.picker) return;
    const size = this.picker.querySelector('[data-custom]')?.closest('[data-group]')?.dataset.group;
    for (const group of this.picker.querySelectorAll('.picker__group')) {
      group.hidden = this.customOn && group.dataset.group !== size && group.dataset.group !== this.debossGroup();
    }
    const text = this.picker.querySelector('[data-deboss]');
    if (text) text.hidden = !this.debossWanted();
  }

  // Rises from the bar and is exactly as wide as its tray; phones span the bar.
  // Measured from the bar's own fixed box, never innerHeight: on iOS innerHeight shrinks while the keyboard
  // is up but the bar does not move, and the panel then came down over the buttons and stayed there.
  place() {
    const bar = this.form.getBoundingClientRect();
    const style = this.picker.style;
    // The on-screen keyboard covers the bar without moving it: the panel then rests on the visible bottom
    // (visualViewport) instead, so the field being typed in stays above the keyboard.
    const view = window.visualViewport;
    const floor = this.getBoundingClientRect().bottom;
    const rest = Math.min(bar.top, view ? view.offsetTop + view.height : bar.top);
    style.setProperty('--picker-bottom', `${floor - rest + 8}px`);
    if (PHONE.matches) {
      style.removeProperty('--picker-left');
      style.removeProperty('--picker-width');
      return;
    }
    style.setProperty('--picker-left', `${bar.left}px`);
    style.setProperty('--picker-width', `${bar.width}px`);
  }

  onToggle(event) {
    const open = event.newState === 'open';
    for (const button of this.querySelectorAll('[data-pick]')) {
      button.setAttribute('aria-expanded', String(open && button === this.opener));
    }
    if (!open) {
      if (this.picker.contains(document.activeElement)) this.opener?.focus({ preventScroll: true });
      this.which = null;
    }
  }

  /* --- Choosing a value --- */

  async onChange(event) {
    if (event.target.closest('[data-custom-panel]')) return this.customUpdate();
    if (!event.target.matches('.picker__input')) return;
    const choice = event.target;
    if (choice.matches('[data-custom]')) return this.setCustom(true);
    if (this.customOn) {
      // Debossing stays a choice of the custom pad; any other size leaves it.
      if (choice.closest('[data-group]')?.dataset.group === this.debossGroup()) {
        this.syncDebossing();
        this.customUpdate();
        if (this.debossWanted()) this.focusDeboss();
        return;
      }
      this.setCustom(false);
    }
    this.syncDebossing();
    const group = choice.closest('[data-group]')?.dataset.group;
    // "Yes Please": the box takes focus now, inside the tap, or iOS does not raise the keyboard. It sits
    // outside the redrawn part of the panel, so the redraw below leaves it, its focus and its text alone.
    const deboss = this.debossWanted() && group === this.debossGroup() && !this.arrowed;
    if (deboss) this.focusDeboss();
    if (!choice.dataset.optionId) return;
    this.abort?.abort();
    const revision = this.revision = (this.revision || 0) + 1;
    this.abort = new AbortController();
    this.swapping = true;
    this.form.querySelector('[data-message]').hidden = true;
    this.form.querySelector('[data-add]').disabled = true;
    const url = new URL(this.dataset.productUrl, location.origin);
    const choices = [...this.picker.querySelectorAll('.picker__input:checked')].map((radio) => radio.dataset.optionId).filter(Boolean);
    url.searchParams.set('option_values', choices.join(','));
    const fallback = url.href;
    url.searchParams.set('section_id', this.dataset.sectionId);
    try {
      const html = await request(url, { signal: this.abort.signal }, 'text');
      if (revision !== this.revision || this.customOn) return;
      const fresh = new DOMParser().parseFromString(html, 'text/html');
      const selected = fresh.querySelector('capra-product-form input[name="id"]');
      if (!selected?.value || SWAPS.some((name) => !fresh.querySelector(`[data-swap="${name}"]`))) throw new Error('section');
      for (const name of SWAPS) {
        const next = fresh.querySelector(`[data-swap="${name}"]`);
        this.section.querySelector(`[data-swap="${name}"]`)?.replaceWith(next);
      }
      this.input.value = selected.value;
      const address = new URL(location.href);
      address.searchParams.set('variant', selected.value);
      address.searchParams.delete('option_values');
      history.replaceState(history.state, '', address);
      this.form.querySelector('[data-message]').hidden = true;
    } catch {
      if (revision !== this.revision) return;
      this.notice(this.dataset.optionsFailed, fallback, this.dataset.updateChoices);
    } finally {
      if (revision === this.revision) this.swapping = false;
    }
    this.syncDebossing();
    if (!this.isOpen()) return;
    this.only();
    if (deboss) {
      // Already focused: only a focus lost to the redraw is given back, never one the visitor moved on purpose.
      // The redraw may have cut the scroll short or moved the box: a no-op when it is still in view.
      const field = this.form.querySelector('.deboss__input');
      if (field && (!document.activeElement || document.activeElement === document.body)) field.focus({ preventScroll: true });
      this.revealDeboss('auto');
    } else {
      this.picker.querySelector(`[data-group="${group}"] .picker__input:checked`)?.focus({ preventScroll: true });
    }
  }

  /* --- A custom size (Desk Pad Organizer) --- */

  setCustom(on) {
    const panel = this.picker?.querySelector('[data-custom-panel]');
    if (!panel) return;
    this.abort?.abort();
    this.revision = (this.revision || 0) + 1;
    this.swapping = false;
    this.customOn = on;
    panel.hidden = !on;
    // The organizer's "+ $25" is not what debossing adds to a custom pad, and in another currency Shopify rounds each
    // band on its own: no one figure is true for every size, so the row says none and the panel's price is the sum.
    const yes = [...this.picker.querySelectorAll(`[data-group="${this.debossGroup()}"] .picker__input`)].find((i) => /^yes/i.test(i.value));
    const meta = yes?.nextElementSibling?.querySelector('.picker__meta');
    if (meta) {
      meta.dataset.was ??= meta.textContent;
      meta.textContent = on ? '' : meta.dataset.was;
    }
    this.only();
    if (!on) return;
    this.customUpdate();
    panel.querySelector('[data-custom-length]')?.focus({ preventScroll: true });
  }

  customUpdate() {
    const panel = this.picker?.querySelector('[data-custom-panel]');
    if (!panel || !this.customOn) return;
    if (panel !== this.ladderOf) {
      try {
        this.ladder = new Ladder(panel.querySelector('[data-custom-ladder]'), panel);
        this.rule = JSON.parse(panel.querySelector('[data-custom-rule]').textContent);
        this.ladderOf = panel;
      } catch {
        this.custom = null;
        this.form.querySelector('[data-add]').disabled = true;
        this.notice(this.dataset.optionsFailed, panel.dataset.ladderUrl, this.dataset.updateChoices);
        return;
      }
    }
    const deboss = this.debossWanted();
    if (!this.ladder.has(deboss)) {
      // The debossing ladder arrives once; until then nothing can be added at a price not yet shown.
      this.custom = null;
      this.form.querySelector('[data-add]')?.setAttribute('disabled', '');
      const price = this.picker.querySelector('.picker__price');
      if (price) price.textContent = '';
      this.querySelector('[data-swap="summary"]').textContent = this.dataset.chooseSize;
      this.ladder.load(deboss).then(() => { if (this.customOn) this.customUpdate(); }, () => {
        if (this.customOn && !this.ladder.has(this.debossWanted())) this.notice(this.dataset.optionsFailed, this.ladder.holder.dataset.ladderUrl, this.dataset.updateChoices);
      });
      return;
    }
    // The site's units (capra-units.js, the size tables' toggle): a switch here converts what is typed.
    const unit = document.documentElement.dataset.units === 'imperial' ? 'in' : 'cm';
    const fields = [panel.querySelector('[data-custom-length]'), panel.querySelector('[data-custom-width]')];
    if (this.customUnit && this.customUnit !== unit) {
      const factor = unit === 'cm' ? 2.54 : 1 / 2.54;
      for (const field of fields) {
        if (field.value) field.value = String(Math.round(parseFloat(field.value) * factor * 10) / 10);
      }
    }
    this.customUnit = unit;
    for (const suffix of panel.querySelectorAll('[data-custom-suffix]')) suffix.textContent = unit;
    const [length, width] = fields.map((field) => parseFloat(field.value));
    const valid = length > 0 && width > 0 && fields.every((field) => field.checkValidity());
    const max = valid ? bandFor(areaSqIn(length, width, unit), this.ladder.areas) : undefined;
    const band = max === undefined ? undefined : this.ladder.band(max, deboss);
    const long = valid && stitched(length, width, unit, this.rule.side_stitch_over_cm);
    panel.querySelector('[data-note="large"]').hidden = !(valid && max === undefined);
    panel.querySelector('[data-note="stitched"]').hidden = !long || max === undefined;
    this.custom = band && { band, size: sizeProperty(length, width, unit), long };
    const price = this.picker.querySelector('.picker__price');
    if (price) price.textContent = band ? band.p : valid && max !== undefined ? this.dataset.unavailable : '';
    const button = this.form.querySelector('[data-add]');
    if (button) button.disabled = !band || this.submitting;
    const summary = this.querySelector('[data-swap="summary"]');
    if (summary) summary.textContent = band ? this.custom.size : this.dataset.chooseSize;
  }

  // What the bag gets for a custom size: the band, the size for the studio, and the debossing text.
  customBody() {
    const panel = this.picker.querySelector('[data-custom-panel]');
    const body = new FormData();
    body.append('id', this.custom.band.i);
    body.append('quantity', '1');
    body.append('properties[Size]', this.custom.size);
    if (this.custom.long) body.append('properties[Construction]', panel.querySelector('[data-custom-construction]').value);
    const text = this.form.querySelector('.deboss__input');
    if (text && !text.disabled && text.value.trim()) body.append('properties[Debossing]', text.value.trim());
    return body;
  }

  /* --- Debossing --- */

  debossGroup() {
    return this.picker?.querySelector('[data-deboss]')?.dataset.group;
  }

  debossWanted() {
    const group = this.debossGroup();
    if (group == null) return false;
    const choice = this.picker.querySelector(`[data-group="${group}"] .picker__input:checked`);
    return Boolean(choice && /^yes/i.test(choice.value));
  }

  syncDebossing() {
    const field = this.form.querySelector('.deboss__input');
    if (!field) return;
    const wanted = this.debossWanted();
    field.required = wanted;
    field.disabled = !wanted; // not posted unless debossing is chosen
    this.only();
    this.count(field);
  }

  count(field) {
    const out = this.form.querySelector('[data-deboss-count]');
    if (out) out.textContent = field.maxLength - field.value.length;
    this.checkStamps(field);
  }

  // Every product entry point enforces the same published stamp constraints.
  checkStamps(field) {
    if (!validateDebossing(field)) this.revealDeboss('auto');
  }

  onType(event) {
    if (event.target.closest('[data-custom-panel]')) return this.customUpdate();
    const field = event.target;
    if (!field.matches('.deboss__input')) return;
    const at = field.selectionStart;
    field.value = field.value.toUpperCase();
    field.setSelectionRange(at, at);
    this.count(field);
  }

  // The field opens below the last choice: focus it and bring it into view in one move
  focusDeboss() {
    const field = this.form.querySelector('.deboss__input');
    if (!field) return;
    field.focus({ preventScroll: true });
    this.revealDeboss(REDUCED.matches ? 'auto' : 'smooth');
  }

  // The box and what appears under it (the error, the final-sale note): as little scroll as that takes,
  // never so much that the field goes under the panel's sticky title.
  revealDeboss(behavior) {
    const box = this.form.querySelector('[data-deboss]');
    if (!box || box.hidden || !this.isOpen()) return;
    requestAnimationFrame(() => {
      const panel = this.picker.getBoundingClientRect();
      const top = this.picker.querySelector('.picker__top')?.getBoundingClientRect().bottom ?? panel.top;
      const { top: start, bottom: end } = box.getBoundingClientRect();
      const by = Math.min(end + 12 - panel.bottom, start - top - 12);
      if (by > 0 || start < top) this.picker.scrollBy({ top: start < top ? start - top - 12 : by, behavior });
    });
  }

  /* --- Add to bag --- */

  async onSubmit(event) {
    event.preventDefault();
    if (this.submitting || this.swapping || this.uncertain) return;
    const field = this.form.querySelector('.deboss__input');
    if (field) validateDebossing(field);
    // Debossing chosen but no text yet: open the panel at the text box.
    if (field && field.required && (!field.value.trim() || !field.checkValidity())) {
      this.open('all');
      this.focusDeboss();
      return;
    }
    // A custom size without a size yet (or past the ladder): open the panel at the fields.
    if (this.customOn && !this.custom) {
      this.open('all');
      this.picker.querySelector('[data-custom-length]')?.focus({ preventScroll: true });
      return;
    }
    const button = this.form.querySelector('[data-add]');
    const message = this.form.querySelector('[data-message]');
    if (!button || button.disabled) return;
    this.submitting = true;
    for (const pick of this.form.querySelectorAll('[data-pick]')) pick.disabled = true;
    if (this.picker) this.picker.inert = true;
    button.disabled = true;
    if (this.isOpen()) this.picker.hidePopover();
    button.classList.remove('is-added');
    button.classList.add('is-adding');
    button.setAttribute('aria-busy', 'true');
    message.hidden = true;
    message.classList.remove('is-error');
    try {
      const body = this.customOn ? this.customBody() : new FormData(this.form);
      const expected = Number(body.get('id'));
      await mutate('add', { headers: { Accept: 'application/json' }, body }, (answer) =>
        (answer.items || [answer]).some((item) => Number(item.variant_id || item.id) === expected && item.quantity > 0));
      this.added(button);
      request(`${ROOT}cart.js`).then((cart) => {
        for (const count of document.querySelectorAll('[data-cart-count]')) {
          count.textContent = cart.item_count;
          count.hidden = cart.item_count === 0;
        }
      }).catch(() => {});
      this.notice(this.dataset.added, null, null, false);
    } catch (error) {
      this.uncertain = !error.refused;
      this.notice(error.shown || (this.uncertain ? this.dataset.checkBag : this.dataset.failed), `${ROOT}cart`, this.dataset.viewBag);
    } finally {
      this.submitting = false;
      button.disabled = Boolean(this.uncertain);
      for (const pick of this.form.querySelectorAll('[data-pick]')) pick.disabled = Boolean(this.uncertain);
      if (this.picker) this.picker.inert = Boolean(this.uncertain);
      button.classList.remove('is-adding');
      button.removeAttribute('aria-busy');
    }
  }

  // "Added" starts to be written at about 0.3s; the header bag fills from then, at the same pace.
  added(button) {
    button.classList.add('is-added');
    setTimeout(() => button.classList.remove('is-added'), 2200);
    navigator.vibrate?.(14); // a small tap where the phone allows it
    document.dispatchEvent(new CustomEvent('capra:added', { detail: { handoff: REDUCED.matches ? 0 : 300 } }));
  }

  // Recovery always has a real destination; messages never disappear solely because a request failed.
  notice(text, href, label, error = true) {
    const message = this.form.querySelector('[data-message]');
    message.replaceChildren(text);
    if (error) {
      const link = Object.assign(document.createElement('a'), { href, textContent: label });
      message.append(' ', link);
    }
    message.classList.toggle('visually-hidden', !error);
    message.classList.toggle('is-error', error);
    message.hidden = false;
  }
}

customElements.define('capra-product-form', CapraProductForm);
